'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  Check, ChevronDown, ArrowRight, Zap, GitCompare, Camera,
  BarChart2, AlertTriangle, Code2, Users, Palette, Layers,
  MousePointer2, Ruler, Type,
} from 'lucide-react';

// ─── Nav ─────────────────────────────────────────────────────────────────────

function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.06] bg-[#0a0a0b]/80 backdrop-blur-xl">
      <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2 font-semibold text-foreground text-sm tracking-tight">
          <span className="w-6 h-6 rounded-md bg-primary flex items-center justify-center shrink-0">
            <GitCompare size={13} className="text-primary-foreground" />
          </span>
          Shipframe
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
          <a href="#product" className="hover:text-foreground transition-colors">Product</a>
          <a href="#how-it-works" className="hover:text-foreground transition-colors">How it works</a>
          <a href="#features" className="hover:text-foreground transition-colors">Features</a>
          <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
          <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link href="/sign-up-login-screen" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Sign in
          </Link>
          <Link href="/sign-up-login-screen" className="btn-primary text-xs px-4 py-2">
            Get started
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 text-muted-foreground hover:text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <div className="w-4 flex flex-col gap-1">
            <span className={`block h-px bg-current transition-all duration-200 ${mobileOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
            <span className={`block h-px bg-current transition-all duration-200 ${mobileOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-px bg-current transition-all duration-200 ${mobileOpen ? '-rotate-45 -translate-y-1.5' : ''}`} />
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/[0.06] bg-[#0a0a0b] px-5 py-4 flex flex-col gap-4">
          {['#product', '#how-it-works', '#features', '#pricing', '#faq']?.map((href) => (
            <a key={href} href={href} onClick={() => setMobileOpen(false)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors capitalize">
              {href?.replace('#', '')?.replace(/-/g, ' ')}
            </a>
          ))}
          <div className="flex flex-col gap-2 pt-2 border-t border-white/[0.06]">
            <Link href="/sign-up-login-screen" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Sign in</Link>
            <Link href="/sign-up-login-screen" className="btn-primary text-xs px-4 py-2 w-fit">Get started</Link>
          </div>
        </div>
      )}
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function HeroVisual() {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#0d0d0f] overflow-hidden shadow-2xl shadow-black/60">
      {/* Window chrome */}
      <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/[0.06] bg-[#0a0a0b]">
        <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
        <span className="ml-3 text-xs text-muted-foreground font-mono">shipframe — visual comparison</span>
        <div className="ml-auto flex items-center gap-2 px-2.5 py-1 rounded-md bg-primary/10 border border-primary/20">
          <BarChart2 size={10} className="text-primary" />
          <span className="text-[10px] font-semibold text-primary">94% match</span>
        </div>
      </div>

      {/* Three-panel comparison */}
      <div className="grid grid-cols-3" style={{ minHeight: 320 }}>

        {/* ── Figma Design panel ── */}
        <div className="border-r border-white/[0.06] p-4 flex flex-col gap-2.5">
          {/* Panel header */}
          <div className="flex items-center gap-2 pb-2 border-b border-white/[0.05]">
            <div className="w-5 h-5 rounded bg-[#1e1e1e] border border-white/10 flex items-center justify-center shrink-0">
              <span className="text-[9px] font-bold text-white leading-none">F</span>
            </div>
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Figma Design</span>
            <div className="ml-auto px-1.5 py-0.5 rounded bg-primary/10 border border-primary/20">
              <span className="text-[8px] font-medium text-primary">Source</span>
            </div>
          </div>

          {/* Navbar */}
          <div className="h-7 rounded-md bg-[#1c1c1e] border border-white/[0.08] flex items-center px-2.5 gap-2 shrink-0">
            <div className="w-2.5 h-2.5 rounded-sm bg-primary/70" />
            <div className="h-1.5 rounded-full bg-white/30 w-9" />
            <div className="flex gap-1.5 ml-2">
              <div className="h-1.5 rounded-full bg-white/15 w-5" />
              <div className="h-1.5 rounded-full bg-white/15 w-5" />
              <div className="h-1.5 rounded-full bg-white/15 w-5" />
            </div>
            <div className="ml-auto h-5 rounded bg-primary/60 w-10 flex items-center justify-center">
              <div className="h-1 rounded-full bg-white/70 w-5" />
            </div>
          </div>

          {/* Hero block */}
          <div className="rounded-lg bg-primary/[0.10] border border-primary/20 p-3 space-y-2 shrink-0">
            <div className="h-2.5 rounded-full bg-primary/55 w-4/5" />
            <div className="h-1.5 rounded-full bg-white/20 w-full" />
            <div className="h-1.5 rounded-full bg-white/15 w-11/12" />
            <div className="h-1.5 rounded-full bg-white/10 w-3/4" />
            <div className="flex gap-2 mt-2.5">
              <div className="h-6 rounded-md bg-primary/75 w-20 flex items-center justify-center">
                <div className="h-1.5 rounded-full bg-white/70 w-10" />
              </div>
              <div className="h-6 rounded-md border border-white/20 w-16 flex items-center justify-center">
                <div className="h-1.5 rounded-full bg-white/30 w-8" />
              </div>
            </div>
          </div>

          {/* Cards row */}
          <div className="grid grid-cols-2 gap-1.5 flex-1">
            {[0, 1]?.map((n) => (
              <div key={n} className="rounded-lg bg-[#1c1c1e] border border-white/[0.08] p-2 flex flex-col gap-1.5">
                <div className="h-8 rounded-md bg-white/[0.06] w-full" />
                <div className="h-1.5 rounded-full bg-white/20 w-full" />
                <div className="h-1.5 rounded-full bg-white/12 w-3/4" />
                <div className="mt-auto h-4 rounded bg-primary/30 w-full flex items-center justify-center">
                  <div className="h-1 rounded-full bg-primary/60 w-8" />
                </div>
              </div>
            ))}
          </div>

          {/* Footer strip */}
          <div className="h-5 rounded-md bg-[#1c1c1e] border border-white/[0.05] flex items-center px-2 gap-1.5 shrink-0">
            <div className="h-1 rounded-full bg-white/15 w-10" />
            <div className="h-1 rounded-full bg-white/10 w-6" />
            <div className="ml-auto h-1 rounded-full bg-white/10 w-8" />
          </div>

          {/* Panel footer */}
          <div className="flex items-center gap-1.5 pt-0.5">
            <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
            <span className="text-[9px] text-muted-foreground font-mono">homepage.fig · frame 1</span>
          </div>
        </div>

        {/* ── Production panel ── */}
        <div className="border-r border-white/[0.06] p-4 flex flex-col gap-2.5">
          {/* Panel header */}
          <div className="flex items-center gap-2 pb-2 border-b border-white/[0.05]">
            <div className="w-5 h-5 rounded bg-[#1c1c1e] border border-white/10 flex items-center justify-center shrink-0">
              <Camera size={9} className="text-white/70" />
            </div>
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Production</span>
            <div className="ml-auto px-1.5 py-0.5 rounded bg-[#3b82f6]/10 border border-[#3b82f6]/20">
              <span className="text-[8px] font-medium text-[#3b82f6]">Live</span>
            </div>
          </div>

          {/* Navbar — slightly different spacing */}
          <div className="h-7 rounded-md bg-[#1c1c1e] border border-white/[0.08] flex items-center px-2.5 gap-2 shrink-0">
            <div className="w-2.5 h-2.5 rounded-sm bg-primary/45" />
            <div className="h-1.5 rounded-full bg-white/30 w-9" />
            <div className="flex gap-1.5 ml-2">
              <div className="h-1.5 rounded-full bg-white/15 w-5" />
              <div className="h-1.5 rounded-full bg-white/15 w-5" />
              <div className="h-1.5 rounded-full bg-white/15 w-5" />
            </div>
            <div className="ml-auto h-5 rounded bg-primary/45 w-12 flex items-center justify-center">
              <div className="h-1 rounded-full bg-white/60 w-6" />
            </div>
          </div>

          {/* Hero block — padding drift (less padding) */}
          <div className="rounded-lg bg-primary/[0.06] border border-primary/12 p-1.5 space-y-2 shrink-0">
            <div className="h-2.5 rounded-full bg-primary/35 w-4/5" />
            <div className="h-1.5 rounded-full bg-white/15 w-full" />
            <div className="h-1.5 rounded-full bg-white/12 w-11/12" />
            <div className="h-1.5 rounded-full bg-white/08 w-3/4" />
            <div className="flex gap-2 mt-2.5">
              <div className="h-6 rounded bg-primary/50 w-20 flex items-center justify-center">
                <div className="h-1.5 rounded-full bg-white/55 w-10" />
              </div>
              <div className="h-6 rounded border border-white/15 w-16 flex items-center justify-center">
                <div className="h-1.5 rounded-full bg-white/25 w-8" />
              </div>
            </div>
          </div>

          {/* Cards row — border-radius drift */}
          <div className="grid grid-cols-2 gap-1.5 flex-1">
            {[0, 1]?.map((n) => (
              <div key={n} className="rounded bg-[#1c1c1e] border border-white/[0.08] p-2 flex flex-col gap-1.5">
                <div className="h-8 rounded-sm bg-white/[0.06] w-full" />
                <div className="h-1.5 rounded-full bg-white/18 w-full" />
                <div className="h-1.5 rounded-full bg-white/10 w-3/4" />
                <div className="mt-auto h-4 rounded-sm bg-primary/20 w-full flex items-center justify-center">
                  <div className="h-1 rounded-full bg-primary/45 w-8" />
                </div>
              </div>
            ))}
          </div>

          {/* Footer strip */}
          <div className="h-5 rounded-md bg-[#1c1c1e] border border-white/[0.05] flex items-center px-2 gap-1.5 shrink-0">
            <div className="h-1 rounded-full bg-white/15 w-10" />
            <div className="h-1 rounded-full bg-white/10 w-6" />
            <div className="ml-auto h-1 rounded-full bg-white/10 w-8" />
          </div>

          {/* Panel footer */}
          <div className="flex items-center gap-1.5 pt-0.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#3b82f6]/60" />
            <span className="text-[9px] text-muted-foreground font-mono">shipframe.io · live</span>
          </div>
        </div>

        {/* ── Visual Diff panel ── */}
        <div className="p-4 flex flex-col gap-2.5 bg-[#0c0c0e]">
          {/* Panel header */}
          <div className="flex items-center gap-2 pb-2 border-b border-white/[0.05]">
            <div className="w-5 h-5 rounded bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
              <GitCompare size={9} className="text-primary" />
            </div>
            <span className="text-[10px] font-semibold text-primary uppercase tracking-wider">Visual Diff</span>
            <div className="ml-auto px-1.5 py-0.5 rounded bg-[#ef4444]/10 border border-[#ef4444]/25">
              <span className="text-[8px] font-medium text-[#ef4444]">3 issues</span>
            </div>
          </div>

          {/* Navbar — clean / ok */}
          <div className="h-7 rounded-md bg-[#1c1c1e] border border-white/[0.05] flex items-center px-2.5 gap-2 shrink-0">
            <div className="h-1 rounded-full bg-white/12 w-20" />
            <div className="ml-auto flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-primary/50" />
              <span className="text-[8px] text-primary/60 font-mono">ok</span>
            </div>
          </div>

          {/* Hero — spacing diff */}
          <div className="rounded-lg border border-[#eab308]/45 bg-[#eab308]/[0.07] p-2 space-y-1.5 relative shrink-0">
            <div className="absolute top-1.5 right-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#eab308]/20 border border-[#eab308]/35">
              <Ruler size={7} className="text-[#eab308]" />
              <span className="text-[8px] text-[#eab308] font-mono">Spacing</span>
            </div>
            <div className="h-2 rounded-full bg-[#eab308]/30 w-4/5" />
            <div className="h-1.5 rounded-full bg-white/10 w-full" />
            <div className="h-1.5 rounded-full bg-white/08 w-11/12" />
            <div className="flex gap-2 mt-2">
              <div className="h-5 rounded bg-[#eab308]/20 border border-[#eab308]/25 w-20" />
              <div className="h-5 rounded border border-white/10 w-16" />
            </div>
          </div>

          {/* Cards — border-radius + component drift */}
          <div className="grid grid-cols-2 gap-1.5 flex-1">
            <div className="rounded border border-[#ef4444]/35 bg-[#ef4444]/[0.06] p-2 flex flex-col gap-1.5 relative">
              <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-[#ef4444] flex items-center justify-center z-10">
                <span className="text-[7px] text-white font-bold leading-none">!</span>
              </div>
              <div className="h-8 rounded-sm bg-[#ef4444]/10 w-full" />
              <div className="h-1.5 rounded-full bg-white/10 w-full" />
              <div className="mt-auto h-4 rounded-sm bg-[#ef4444]/15 w-full flex items-center justify-center">
                <span className="text-[7px] text-[#ef4444]/70 font-mono">Component drift</span>
              </div>
            </div>
            <div className="rounded border border-[#f97316]/30 bg-[#f97316]/[0.05] p-2 flex flex-col gap-1.5 relative">
              <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-[#f97316] flex items-center justify-center z-10">
                <span className="text-[7px] text-white font-bold leading-none">!</span>
              </div>
              <div className="h-8 rounded-sm bg-[#f97316]/10 w-full" />
              <div className="h-1.5 rounded-full bg-white/10 w-full" />
              <div className="mt-auto h-4 rounded-sm bg-[#f97316]/12 w-full flex items-center justify-center">
                <span className="text-[7px] text-[#f97316]/70 font-mono">Typography</span>
              </div>
            </div>
          </div>

          {/* Footer strip — ok */}
          <div className="h-5 rounded-md bg-[#1c1c1e] border border-white/[0.05] flex items-center px-2 gap-1.5 shrink-0">
            <div className="h-1 rounded-full bg-white/10 w-10" />
            <div className="ml-auto flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-primary/50" />
              <span className="text-[8px] text-primary/60 font-mono">ok</span>
            </div>
          </div>

          {/* Issue list */}
          <div className="space-y-1 pt-1 border-t border-white/[0.05]">
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-[#eab308]/[0.08] border border-[#eab308]/20">
              <Ruler size={8} className="text-[#eab308] shrink-0" />
              <span className="text-[8px] text-[#eab308] font-medium flex-1 leading-tight">Hero padding drift</span>
              <span className="text-[8px] font-mono text-[#eab308]/70 tabular-nums">48→32px</span>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-[#ef4444]/[0.08] border border-[#ef4444]/20">
              <MousePointer2 size={8} className="text-[#ef4444] shrink-0" />
              <span className="text-[8px] text-[#ef4444] font-medium flex-1 leading-tight">Card border-radius</span>
              <span className="text-[8px] font-mono text-[#ef4444]/70 tabular-nums">12→4px</span>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-[#f97316]/[0.08] border border-[#f97316]/20">
              <Type size={8} className="text-[#f97316] shrink-0" />
              <span className="text-[8px] text-[#f97316] font-medium flex-1 leading-tight">CTA font-weight</span>
              <span className="text-[8px] font-mono text-[#f97316]/70 tabular-nums">700→600</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom status bar */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-t border-white/[0.06] bg-[#0a0a0b]">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-[10px] text-muted-foreground font-mono">Similarity</span>
            <span className="text-[10px] font-semibold text-primary font-mono tabular-nums">94%</span>
          </div>
          <div className="w-px h-3 bg-white/[0.08]" />
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#ef4444]" />
            <span className="text-[10px] text-muted-foreground font-mono">Issues detected</span>
            <span className="text-[10px] font-semibold text-[#ef4444] font-mono tabular-nums">3</span>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          {[
            { label: 'Layout', score: 96, color: 'bg-primary' },
            { label: 'Spacing', score: 87, color: 'bg-[#eab308]' },
            { label: 'Typography', score: 91, color: 'bg-primary' },
          ]?.map((s) => (
            <div key={s?.label} className="flex items-center gap-1.5">
              <span className="text-[9px] text-muted-foreground">{s?.label}</span>
              <div className="w-12 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                <div className={`h-full rounded-full ${s?.color}`} style={{ width: `${s?.score}%`, opacity: 0.75 }} />
              </div>
              <span className="text-[9px] text-foreground font-mono tabular-nums">{s?.score}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="pt-32 pb-20 px-5 relative overflow-hidden" id="product">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-primary/[0.05] rounded-full blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto text-center relative">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/20 bg-primary/[0.07] text-primary text-xs font-medium mb-8">
          <Zap size={11} />
          Built for designers &amp; developers
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground tracking-tight leading-[1.1] mb-6">
          Ship what you designed.
        </h1>

        <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed">
          Compare your Figma designs with production websites and catch visual differences before your users do.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link href="/sign-up-login-screen" className="btn-primary px-6 py-3 text-sm font-semibold w-full sm:w-auto justify-center">
            Start comparing — free
            <ArrowRight size={15} />
          </Link>
          <a href="#how-it-works" className="btn-secondary px-6 py-3 text-sm w-full sm:w-auto justify-center">
            See how it works
          </a>
        </div>
      </div>

      {/* Hero visual */}
      <div className="max-w-5xl mx-auto mt-16 relative">
        <HeroVisual />
      </div>
    </section>
  );
}

// ─── Problem ──────────────────────────────────────────────────────────────────

function Problem() {
  const drifts = [
    { label: 'Spacing changes', icon: <Ruler size={13} />, color: '#eab308', bg: 'rgba(234,179,8,0.07)', border: 'rgba(234,179,8,0.18)' },
    { label: 'Typography differences', icon: <Type size={13} />, color: '#f97316', bg: 'rgba(249,115,22,0.07)', border: 'rgba(249,115,22,0.18)' },
    { label: 'Component drift', icon: <MousePointer2 size={13} />, color: '#ef4444', bg: 'rgba(239,68,68,0.07)', border: 'rgba(239,68,68,0.18)' },
  ];

  return (
    <section className="py-20 px-5 border-t border-white/[0.06]">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-4">The problem</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight mb-5">
              Your Figma file says one thing. Production sometimes says another.
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-8">
              Shipframe catches these differences before your users do.
            </p>
            <div className="space-y-2.5">
              {drifts?.map((d) => (
                <div key={d?.label} className="flex items-center gap-3 px-4 py-3 rounded-xl border"
                  style={{ background: d?.bg, borderColor: d?.border }}>
                  <span style={{ color: d?.color }}>{d?.icon}</span>
                  <span className="text-sm font-medium" style={{ color: d?.color }}>{d?.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Visual diff card */}
          <div className="rounded-2xl border border-white/[0.08] bg-card p-6 space-y-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Design vs. Production</span>
              <span className="text-xs text-[#ef4444] font-semibold bg-[#ef4444]/10 border border-[#ef4444]/20 px-2 py-0.5 rounded-md">3 issues</span>
            </div>
            {[
              { label: 'Button padding', design: '12px', prod: '8px', severity: '#eab308', pct: 65 },
              { label: 'Heading font-size', design: '32px', prod: '28px', severity: '#f97316', pct: 75 },
              { label: 'Card border-radius', design: '12px', prod: '4px', severity: '#ef4444', pct: 85 },
            ]?.map((item) => (
              <div key={item?.label} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{item?.label}</span>
                  <span className="font-mono text-[11px]" style={{ color: item?.severity }}>
                    {item?.design} → {item?.prod}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${item?.pct}%`, background: item?.severity, opacity: 0.65 }} />
                </div>
              </div>
            ))}
            <div className="pt-2 flex items-center gap-2 text-xs text-muted-foreground border-t border-white/[0.06]">
              <AlertTriangle size={11} className="text-[#ef4444]" />
              <span>Caught before reaching users</span>
              <span className="ml-auto text-primary font-semibold">Shipframe</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── How it works ─────────────────────────────────────────────────────────────

function HowItWorks() {
  const steps = [
    {
      num: '01',
      title: 'Connect your Figma design',
      desc: 'Paste your Figma file URL. Shipframe fetches your design frames directly via the Figma API — no exports, no manual screenshots.',
      icon: <Layers size={18} className="text-primary" />,
    },
    {
      num: '02',
      title: 'Compare against production',
      desc: 'Enter your live URL. Shipframe screenshots your production site with a headless browser and runs a pixel-level comparison.',
      icon: <GitCompare size={18} className="text-primary" />,
    },
    {
      num: '03',
      title: 'Fix before shipping',
      desc: 'Get a detailed diff report with similarity scores, highlighted regions, and actionable issue descriptions — ready to share with your team.',
      icon: <Code2 size={18} className="text-primary" />,
    },
  ];

  return (
    <section className="py-20 px-5 border-t border-white/[0.06]" id="how-it-works">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">How it works</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Three steps to zero design drift</h2>
        </div>

        {/* Equal-height 3-column grid with connector line on desktop */}
        <div className="relative">
          {/* Connector line — desktop only */}
          <div className="hidden md:block absolute top-[2.75rem] left-[calc(16.666%+1.5rem)] right-[calc(16.666%+1.5rem)] h-px bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 pointer-events-none" aria-hidden />

          <div className="grid md:grid-cols-3 gap-4 items-stretch">
            {steps?.map((step, i) => (
              <div
                key={step?.num}
                className="rounded-2xl border border-white/[0.08] bg-card p-6 flex flex-col gap-4 hover:border-primary/20 transition-colors duration-200"
              >
                {/* Icon + number row */}
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 relative z-10 bg-card">
                    {step?.icon}
                  </div>
                  <span className="text-4xl font-bold text-white/[0.05] font-mono select-none">{step?.num}</span>
                </div>
                {/* Content */}
                <div className="flex flex-col gap-2 flex-1">
                  <h3 className="text-sm font-semibold text-foreground">{step?.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{step?.desc}</p>
                </div>
                {/* Step indicator dot */}
                <div className="flex items-center gap-2 pt-2 border-t border-white/[0.05]">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                  <span className="text-[10px] text-muted-foreground font-mono">Step {i + 1} of 3</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Features ─────────────────────────────────────────────────────────────────

function Features() {
  const features = [
    {
      icon: <GitCompare size={16} className="text-primary" />,
      title: 'Visual Regression Detection',
      desc: 'Pixel-level comparison catches even subtle layout shifts between design and production.',
    },
    {
      icon: <Layers size={16} className="text-primary" />,
      title: 'Figma Integration',
      desc: 'Connect directly via the Figma API. No exports, no manual screenshots needed.',
    },
    {
      icon: <Camera size={16} className="text-primary" />,
      title: 'Production Screenshots',
      desc: 'Automated headless browser captures your live site at the exact viewport you specify.',
    },
    {
      icon: <BarChart2 size={16} className="text-primary" />,
      title: 'Similarity Scoring',
      desc: 'A 0–100% match score gives you an at-a-glance health check for every comparison.',
    },
    {
      icon: <AlertTriangle size={16} className="text-primary" />,
      title: 'Issue Detection',
      desc: 'Automatically flags spacing, typography, color, and component drift with severity levels.',
    },
    {
      icon: <Code2 size={16} className="text-primary" />,
      title: 'Developer-Friendly Workflow',
      desc: 'Integrate into your CI pipeline or run on-demand before every release.',
    },
  ];

  return (
    <section className="py-20 px-5 border-t border-white/[0.06]" id="features">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Features</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Everything you need to ship with confidence</h2>
        </div>

        {/* Uniform 3-col grid — all cards equal height per row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features?.map((f) => (
            <div
              key={f?.title}
              className="rounded-2xl border border-white/[0.08] bg-card p-6 flex flex-col gap-4 hover:border-primary/20 transition-colors duration-200"
            >
              <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                {f?.icon}
              </div>
              <div className="flex flex-col gap-1.5 flex-1">
                <h3 className="text-sm font-semibold text-foreground">{f?.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f?.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Demo ─────────────────────────────────────────────────────────────────────

function Demo() {
  return (
    <section className="py-20 px-5 border-t border-white/[0.06]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Demo</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
            See the difference before your users do.
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            The numbers below are illustrative examples of what Shipframe surfaces in a typical comparison run.
          </p>
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-card overflow-hidden">
          {/* Top bar */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] bg-[#0d0d0f]">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-medium text-foreground font-mono">homepage.figma → shipframe.io</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
              <BarChart2 size={12} className="text-primary" />
              <span className="text-xs font-semibold text-primary">91% match</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/[0.06]">
            {/* Issues */}
            <div className="p-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Issues detected</h4>
              <div className="space-y-2.5">
                {[
                  { label: 'Hero padding-top', severity: 'medium', detail: '48px → 32px' },
                  { label: 'CTA font-weight', severity: 'low', detail: '700 → 600' },
                  { label: 'Nav link color', severity: 'low', detail: '#fafafa → #a1a1aa' },
                ]?.map((issue) => (
                  <div key={issue?.label} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <span className={`badge-${issue?.severity}`}>{issue?.severity}</span>
                    <span className="text-xs text-foreground flex-1 min-w-0 truncate">{issue?.label}</span>
                    <span className="text-xs font-mono text-muted-foreground shrink-0">{issue?.detail}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Score breakdown */}
            <div className="p-6">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Score breakdown</h4>
              <div className="space-y-3.5">
                {[
                  { label: 'Layout', score: 96 },
                  { label: 'Typography', score: 88 },
                  { label: 'Colors', score: 94 },
                  { label: 'Spacing', score: 87 },
                ]?.map((item) => (
                  <div key={item?.label} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{item?.label}</span>
                      <span className="text-foreground font-semibold tabular-nums">{item?.score}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full rounded-full bg-primary"
                        style={{ width: `${item?.score}%`, opacity: 0.8 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Who it's for ─────────────────────────────────────────────────────────────

function WhoItsFor() {
  const personas = [
    {
      icon: <Palette size={20} className="text-primary" />,
      title: 'Designers',
      desc: 'Verify your designs are implemented exactly as intended. No more "it looks different in production" surprises.',
    },
    {
      icon: <Code2 size={20} className="text-primary" />,
      title: 'Developers',
      desc: 'Catch visual regressions before they reach users. Integrate into your workflow and ship with confidence.',
    },
    {
      icon: <Users size={20} className="text-primary" />,
      title: 'Teams',
      desc: 'A shared source of truth for design quality. Everyone sees the same diff, speaks the same language.',
    },
  ];

  return (
    <section className="py-20 px-5 border-t border-white/[0.06]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Who it&apos;s for</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Built for the people who care about quality</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-4 items-stretch">
          {personas?.map((p) => (
            <div key={p?.title} className="rounded-2xl border border-white/[0.08] bg-card p-6 flex flex-col gap-4 hover:border-primary/20 transition-colors duration-200">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                {p?.icon}
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <h3 className="text-sm font-semibold text-foreground">{p?.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{p?.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '/month',
    desc: 'Get started with visual testing',
    features: ['5 projects', '5 comparisons/month', 'Figma Integration', 'Visual Diff', 'Issue Detection', 'Comparison History'],
    extras: [],
    cta: 'Get started free',
    href: '/sign-up-login-screen',
    highlight: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    price: '$10',
    period: '/month',
    desc: 'For growing teams and projects',
    features: ['10 projects', '100 comparisons/month', 'Figma Integration', 'Visual Diff', 'Issue Detection', 'Comparison History'],
    extras: [],
    cta: 'Start with Starter',
    href: '/sign-up-login-screen',
    highlight: false,
    badge: 'Popular',
  },
  {
    id: 'scale',
    name: 'Scale',
    price: '$35',
    period: '/month',
    desc: 'For teams that need more power',
    features: ['Unlimited projects', '350 comparisons/month', 'Figma Integration', 'Visual Diff', 'Issue Detection', 'Comparison History'],
    extras: ['Team Collaboration', 'Priority Support'],
    cta: 'Scale up',
    href: '/sign-up-login-screen',
    highlight: true,
  },
];

function Pricing() {
  return (
    <section className="py-20 px-5 border-t border-white/[0.06]" id="pricing">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Pricing</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">Simple, transparent pricing</h2>
          <p className="text-sm text-muted-foreground">Start free. Upgrade when you need more.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 items-stretch">
          {PLANS?.map((plan) => (
            <div key={plan?.id}
              className={`rounded-2xl border p-6 flex flex-col gap-5 relative ${plan?.highlight
                ? 'border-primary/40 bg-primary/[0.04]'
                : 'border-white/[0.08] bg-card'
              }`}>
              {plan?.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold whitespace-nowrap">
                  {plan?.badge}
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-foreground mb-1">{plan?.name}</h3>
                <p className="text-xs text-muted-foreground">{plan?.desc}</p>
              </div>

              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-foreground tabular-nums">{plan?.price}</span>
                <span className="text-sm text-muted-foreground">{plan?.period}</span>
              </div>

              <ul className="space-y-2.5 flex-1">
                {plan?.features?.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-xs text-muted-foreground">
                    <Check size={12} className="text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
                {plan?.extras?.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-xs text-foreground font-medium">
                    <Check size={12} className="text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link href={plan?.href}
                className={plan?.highlight ? 'btn-primary justify-center text-sm' : 'btn-secondary justify-center text-sm'}>
                {plan?.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: 'What is Shipframe?',
    a: 'Shipframe is a visual comparison tool that compares your Figma designs with live production websites, surfacing pixel-level differences so you can fix them before users notice.',
  },
  {
    q: 'How does Figma vs production comparison work?',
    a: 'You provide a Figma file URL and a production URL. Shipframe fetches your Figma frames via the API, screenshots your live site with a headless browser, then runs a pixel-level diff and generates a detailed report.',
  },
  {
    q: 'Does it work with existing websites?',
    a: 'Yes. Shipframe works with any publicly accessible URL — no code changes or integrations required on your production site.',
  },
  {
    q: 'How accurate is the comparison?',
    a: 'Shipframe uses pixel-level diffing combined with AI-assisted region detection. Accuracy depends on viewport consistency, but most comparisons surface issues with sub-pixel precision.',
  },
  {
    q: 'Is Figma data secure?',
    a: 'Your Figma access token is stored encrypted and used only to fetch the frames you specify. We never store your full Figma file — only the rendered frame images needed for comparison.',
  },
  {
    q: 'Can designers and developers use it together?',
    a: 'Absolutely. Shipframe is built for cross-functional teams. Designers can verify implementation fidelity; developers can catch regressions before shipping. Scale plan includes team collaboration features.',
  },
];

function FAQ() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section className="py-20 px-5 border-t border-white/[0.06]" id="faq">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">FAQ</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Common questions</h2>
        </div>

        <div className="space-y-2">
          {FAQS?.map((faq, i) => (
            <div key={i} className="rounded-xl border border-white/[0.08] bg-card overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-foreground hover:bg-white/[0.02] transition-colors gap-4"
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
              >
                <span className="flex-1">{faq?.q}</span>
                <ChevronDown size={15} className={`text-muted-foreground shrink-0 transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`} />
              </button>
              {open === i && (
                <div className="px-5 pb-5 text-xs text-muted-foreground leading-relaxed border-t border-white/[0.06] pt-3">
                  {faq?.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ────────────────────────────────────────────────────────────────

function FinalCTA() {
  return (
    <section className="py-24 px-5 border-t border-white/[0.06] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/[0.05] rounded-full blur-3xl" />
      </div>
      <div className="max-w-2xl mx-auto text-center relative">
        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4 tracking-tight">
          Stop shipping design drift.
        </h2>
        <p className="text-base text-muted-foreground mb-10 leading-relaxed">
          Compare your design with production before your users do.
        </p>
        <Link href="/sign-up-login-screen" className="btn-primary px-8 py-3.5 text-sm font-semibold inline-flex items-center gap-2">
          Start using Shipframe
          <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="border-t border-white/[0.06] px-5 py-12">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-10">
          {/* Brand */}
          <div className="flex flex-col gap-3 max-w-xs">
            <div className="flex items-center gap-2 font-semibold text-foreground text-sm">
              <span className="w-6 h-6 rounded-md bg-primary flex items-center justify-center shrink-0">
                <GitCompare size={13} className="text-primary-foreground" />
              </span>
              Shipframe
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">Ship what you designed.</p>
          </div>

          {/* Links */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 text-xs">
            <div className="flex flex-col gap-3">
              <span className="text-foreground font-semibold">Product</span>
              {[
                { href: '#features', label: 'Features' },
                { href: '#how-it-works', label: 'How it works' },
                { href: '#pricing', label: 'Pricing' },
                { href: '#faq', label: 'FAQ' },
              ]?.map((link) => (
                <a key={link?.href} href={link?.href} className="text-muted-foreground hover:text-foreground transition-colors">
                  {link?.label}
                </a>
              ))}
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-foreground font-semibold">Company</span>
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">Contact</a>
            </div>
            <div className="flex flex-col gap-3">
              <span className="text-foreground font-semibold">Legal</span>
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">Privacy</a>
              <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">Terms</a>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">© 2026 Shipframe. All rights reserved.</p>
          <p className="text-xs text-muted-foreground">Ship what you designed.</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />
      <main>
        <Hero />
        <Problem />
        <HowItWorks />
        <Features />
        <Demo />
        <WhoItsFor />
        <Pricing />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}